import Phaser from 'phaser'

export interface Velocity {
  x: number
  y: number
}

export class VirtualJoystick {
  private scene: Phaser.Scene
  private base: Phaser.GameObjects.Graphics
  private stick: Phaser.GameObjects.Graphics
  private baseX: number = 0
  private baseY: number = 0
  private stickX: number = 0
  private stickY: number = 0
  private radius: number = 60
  private innerRadius: number = 25
  private velocityX: number = 0
  private velocityY: number = 0
  private pointer: Phaser.Input.Pointer | null = null

  public isActive: boolean = false

  constructor(scene: Phaser.Scene) {
    this.scene = scene

    // Create joystick graphics
    this.base = this.createBase()
    this.stick = this.createStick()
    this.drawJoystick()

    // Set up input handlers
    this.setupInput()
  }

  private createBase(): Phaser.GameObjects.Graphics {
    const base = this.scene.add.graphics()
    base.setDepth(100)
    base.setAlpha(0)
    return base
  }

  private createStick(): Phaser.GameObjects.Graphics {
    const stick = this.scene.add.graphics()
    stick.setDepth(101)
    stick.setAlpha(0)
    return stick
  }

  private drawJoystick(): void {
    // Draw base
    this.base.clear()
    this.base.lineStyle(3, 0xffffff, 0.5)
    this.base.fillStyle(0x000000, 0.3)
    this.base.fillCircle(0, 0, this.radius)
    this.base.strokeCircle(0, 0, this.radius)

    // Draw stick
    this.stick.clear()
    this.stick.fillStyle(0xffffff, 0.7)
    this.stick.fillCircle(0, 0, this.innerRadius)

    // Add gradient effect to stick
    this.stick.lineStyle(2, 0xcccccc, 0.8)
    this.stick.strokeCircle(0, 0, this.innerRadius)
  }

  private setupInput(): void {
    // Only activate in bottom-left area of screen for movement
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Check if touch is in the left half of the screen
      if (pointer.x < this.scene.scale.width * 0.5 &&
          pointer.y > this.scene.scale.height * 0.3) {
        this.activate(pointer)
      }
    })

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isActive && this.pointer && this.pointer.id === pointer.id) {
        this.updateStick(pointer)
      }
    })

    this.scene.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.isActive && this.pointer && this.pointer.id === pointer.id) {
        this.deactivate()
      }
    })

    this.scene.input.on('pointerupoutside', (pointer: Phaser.Input.Pointer) => {
      if (this.isActive && this.pointer && this.pointer.id === pointer.id) {
        this.deactivate()
      }
    })
  }

  private activate(pointer: Phaser.Input.Pointer): void {
    this.isActive = true
    this.pointer = pointer
    this.baseX = pointer.x
    this.baseY = pointer.y
    this.stickX = pointer.x
    this.stickY = pointer.y

    // Position and show joystick
    this.base.setPosition(this.baseX, this.baseY)
    this.stick.setPosition(this.stickX, this.stickY)

    // Fade in
    this.scene.tweens.add({
      targets: [this.base, this.stick],
      alpha: 1,
      duration: 100
    })
  }

  private updateStick(pointer: Phaser.Input.Pointer): void {
    const dx = pointer.x - this.baseX
    const dy = pointer.y - this.baseY
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance <= this.radius) {
      this.stickX = pointer.x
      this.stickY = pointer.y
    } else {
      // Clamp to radius
      const angle = Math.atan2(dy, dx)
      this.stickX = this.baseX + Math.cos(angle) * this.radius
      this.stickY = this.baseY + Math.sin(angle) * this.radius
    }

    this.stick.setPosition(this.stickX, this.stickY)

    // Calculate normalized velocity (-1 to 1)
    const clampedDistance = Math.min(distance, this.radius)
    const normalizedDistance = clampedDistance / this.radius

    if (distance > 10) { // Dead zone
      const angle = Math.atan2(dy, dx)
      this.velocityX = Math.cos(angle) * normalizedDistance
      this.velocityY = Math.sin(angle) * normalizedDistance
    } else {
      this.velocityX = 0
      this.velocityY = 0
    }
  }

  private deactivate(): void {
    this.isActive = false
    this.pointer = null
    this.velocityX = 0
    this.velocityY = 0

    // Fade out
    this.scene.tweens.add({
      targets: [this.base, this.stick],
      alpha: 0,
      duration: 150
    })
  }

  public getVelocity(): Velocity {
    return {
      x: this.velocityX,
      y: this.velocityY
    }
  }

  public destroy(): void {
    this.base.destroy()
    this.stick.destroy()
  }
}
